import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, concat, from, of, timer } from 'rxjs';
import { catchError, map, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import {
  CreateWompiPaymentOrderRequest,
  PaymentOrderStatusResponse,
  WompiPaymentOrderResponse
} from '../models/payment.model';
import { WompiPaymentsApiService } from './wompi-payments-api.service';
import { WompiWidgetLoaderService } from './wompi-widget-loader.service';

export type PaymentFlowPhase =
  | 'idle'
  | 'checking_pending'
  | 'creating_order'
  | 'widget_open'
  | 'pending_confirmation'
  | 'approved'
  | 'declined'
  | 'voided'
  | 'expired'
  | 'error';

export interface PaymentFlowSnapshot {
  phase: PaymentFlowPhase;
  order?: WompiPaymentOrderResponse;
  status?: PaymentOrderStatusResponse;
  message?: string;
}

export const PENDING_WOMPI_PAYMENT_ORDER_KEY = 'pendingWompiPaymentOrderId';
const LEGACY_ORDER_KEY = 'last_wompi_payment_order_id';
const PAYMENT_POLL_INTERVAL_MS = 3_000;
const PAYMENT_POLL_MAX_ATTEMPTS = 41;
const TERMINAL_PHASES: PaymentFlowPhase[] = ['approved', 'declined', 'voided', 'expired', 'error'];

@Injectable({ providedIn: 'root' })
export class PaymentFlowStateService {
  private readonly stateSubject = new BehaviorSubject<PaymentFlowSnapshot>({ phase: 'idle' });
  constructor(
    private readonly paymentsApi: WompiPaymentsApiService,
    private readonly widgetLoader: WompiWidgetLoaderService
  ) {}

  getSnapshot(): PaymentFlowSnapshot {
    return this.stateSubject.value;
  }

  createOrderAndOpenWidget(body: CreateWompiPaymentOrderRequest, redirectUrl?: string): Observable<PaymentFlowSnapshot> {
    this.stateSubject.next({ phase: 'checking_pending' });
    return this.getPendingOrderFromSession().pipe(
      switchMap((pending) => {
        if (pending && pending.status === 'pending') {
          const pendingSnapshot: PaymentFlowSnapshot = { phase: 'pending_confirmation', status: pending };
          this.stateSubject.next(pendingSnapshot);
          return concat(of(pendingSnapshot), this.pollOrderStatus(pending.paymentOrderId));
        }

        this.stateSubject.next({ phase: 'creating_order' });
        return this.paymentsApi.createPaymentOrder(body).pipe(
          tap((order) => this.persistOrderId(order.paymentOrderId)),
          switchMap((order) => {
            const widgetSnapshot: PaymentFlowSnapshot = { phase: 'widget_open', order };
            this.stateSubject.next(widgetSnapshot);
            return concat(
              of(widgetSnapshot),
              from(this.widgetLoader.openCheckout(order, redirectUrl)).pipe(
                switchMap(() => this.pollOrderStatus(order.paymentOrderId))
              )
            ).pipe(
              catchError((err) => {
                const snapshot: PaymentFlowSnapshot = {
                  phase: 'error',
                  order,
                  message: this.formatError(err)
                };
                this.stateSubject.next(snapshot);
                return of(snapshot);
              })
            );
          })
        );
      }),
      catchError((err) => {
        const snapshot: PaymentFlowSnapshot = { phase: 'error', message: this.formatError(err) };
        this.stateSubject.next(snapshot);
        return of(snapshot);
      })
    );
  }

  pollOrderStatus(paymentOrderId: number): Observable<PaymentFlowSnapshot> {
    return timer(0, PAYMENT_POLL_INTERVAL_MS).pipe(
      take(PAYMENT_POLL_MAX_ATTEMPTS),
      switchMap(() => this.refreshOrderStatus(paymentOrderId)),
      takeWhile((snapshot) => !this.isTerminal(snapshot), true)
    );
  }

  refreshOrderStatus(paymentOrderId: number): Observable<PaymentFlowSnapshot> {
    return this.paymentsApi.getPaymentOrderStatus(paymentOrderId).pipe(
      map((status) => this.toSnapshot(status)),
      tap((snapshot) => {
        if (this.isTerminal(snapshot)) {
          this.clearPendingOrder();
        }
        this.stateSubject.next(snapshot);
      })
    );
  }

  getPendingOrderFromSession(): Observable<PaymentOrderStatusResponse | null> {
    const id = this.readPersistedOrderId();
    if (!id) {
      return of(null);
    }
    return this.paymentsApi.getPaymentOrderStatus(id).pipe(
      catchError(() => {
        this.clearPendingOrder();
        return of(null);
      })
    );
  }

  readPersistedOrderId(): number | null {
    return this.readOrderIdFromStorage(sessionStorage, PENDING_WOMPI_PAYMENT_ORDER_KEY)
      ?? this.readOrderIdFromStorage(sessionStorage, LEGACY_ORDER_KEY)
      ?? this.readOrderIdFromStorage(localStorage, LEGACY_ORDER_KEY);
  }

  clearPendingOrder(): void {
    this.removeOrderIdFromStorage(sessionStorage, PENDING_WOMPI_PAYMENT_ORDER_KEY);
    this.removeOrderIdFromStorage(sessionStorage, LEGACY_ORDER_KEY);
    this.removeOrderIdFromStorage(localStorage, LEGACY_ORDER_KEY);
  }

  private persistOrderId(paymentOrderId: number): void {
    this.writeOrderIdToStorage(sessionStorage, PENDING_WOMPI_PAYMENT_ORDER_KEY, paymentOrderId);
  }

  private readOrderIdFromStorage(storage: Storage, key: string): number | null {
    try {
      const raw = storage.getItem(key);
      const id = raw ? Number(raw) : NaN;
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }

  private removeOrderIdFromStorage(storage: Storage, key: string): void {
    try {
      storage.removeItem(key);
    } catch {
      // Browser storage may be unavailable in restricted contexts.
    }
  }

  private writeOrderIdToStorage(storage: Storage, key: string, paymentOrderId: number): void {
    try {
      storage.setItem(key, String(paymentOrderId));
    } catch {
      // Browser storage may be unavailable in restricted contexts.
    }
  }

  private toSnapshot(status: PaymentOrderStatusResponse): PaymentFlowSnapshot {
    const normalizedStatus = String(status.status || '').trim().toLowerCase();

    switch (normalizedStatus) {
      case 'approved':
        return { phase: 'approved', status };
      case 'declined':
        return { phase: 'declined', status };
      case 'error':
        return { phase: 'error', status };
      case 'voided':
        return { phase: 'voided', status };
      case 'expired':
        return { phase: 'expired', status };
      default:
        return { phase: 'pending_confirmation', status };
    }
  }

  private isTerminal(snapshot: PaymentFlowSnapshot): boolean {
    return TERMINAL_PHASES.includes(snapshot.phase);
  }

  private formatError(error: unknown): string {
    if (error && typeof error === 'object' && 'status' in error) {
      switch ((error as { status?: number }).status) {
        case 400:
          return 'payments.error.invalid_request';
        case 401:
          return 'payments.error.session';
        case 403:
          return 'payments.error.forbidden';
        case 503:
          return 'payments.error.unconfigured';
        default:
          break;
      }
    }
    if (error instanceof Error) {
      return error.message;
    }
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message?: unknown }).message || error);
    }
    return String(error);
  }
}
